
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '@/components/ui/card';
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Plus, Edit, Trash2, Save, X, AlertTriangle, FileText, Eye } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Expense, BudgetCategory, User } from '@/entities/all';
import ExpenseForm from './ExpenseForm';
import AddBudgetCategoryForm from './AddBudgetCategoryForm';
import BudgetWaterfallChart from './BudgetWaterfallChart';
import ExpenseDetailModal from './ExpenseDetailModal';
import { budgetStructure, expenseStatusOptions } from './budgetConfig';

const currencySymbols = { EUR: '€', USD: '$', MAD: 'DH' };

const formatCurrency = (value, currency) => {
    const symbol = currencySymbols[currency] || currency;
    if (typeof value !== 'number') return `0 ${symbol}`;
    return `${new Intl.NumberFormat('fr-FR').format(value)} ${symbol}`;
};

export default function BudgetTab({ project }) {
    const [users, setUsers] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [budgetCategories, setBudgetCategories] = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [editingBudgets, setEditingBudgets] = useState({});
    const [tempBudgets, setTempBudgets] = useState({});
    const [isAddCategoryFormOpen, setIsAddCategoryFormOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [isExpenseDetailOpen, setIsExpenseDetailOpen] = useState(false);

    const projectCurrency = project?.currency || 'EUR';

    const loadData = useCallback(async () => {
        if (!project) return;
        const [projectExpenses, projectCategories, allUsers] = await Promise.all([
            Expense.filter({ project_id: project.id }),
            BudgetCategory.filter({ project_id: project.id }),
            User.list()
        ]);
        setExpenses(projectExpenses);
        setBudgetCategories(projectCategories);
        setUsers(allUsers);
    }, [project]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const budgetMetrics = useMemo(() => {
        const engaged = expenses.filter(e => ['engaged', 'invoiced', 'paid'].includes(e.status)).reduce((sum, e) => sum + e.amount_ttc, 0);
        const spent = expenses.filter(e => e.status === 'paid').reduce((sum, e) => sum + e.amount_ttc, 0);
        return {
            initial: project.total_budget || 0,
            engaged,
            spent,
            remaining: (project.total_budget || 0) - engaged,
        };
    }, [expenses, project.total_budget]);

    const categoryData = useMemo(() => {
        // Now iterating through actual budgetCategories (which include manually added ones)
        return budgetCategories.map(bc => {
            const allocated = bc.allocated_budget || 0;
            const categoryExpenses = expenses.filter(e => e.category_key === bc.category_key);
            const spent = categoryExpenses.reduce((sum, e) => sum + e.amount_ttc, 0);
            const progress = allocated > 0 ? (spent / allocated) * 100 : 0;

            let alertColor = 'bg-green-500';
            if (progress > 100) alertColor = 'bg-red-500';
            else if (progress > 90) alertColor = 'bg-orange-500';
            else if (progress > 75) alertColor = 'bg-yellow-500';

            return {
                key: bc.category_key,
                name: bc.category_name,
                allocated,
                spent,
                progress,
                alertColor,
                expenses: categoryExpenses,
            };
        });
    }, [budgetCategories, expenses]);

    const totalAllocated = useMemo(() => {
        return budgetCategories.reduce((sum, bc) => sum + (bc.allocated_budget || 0), 0);
    }, [budgetCategories]);

    const handleSaveExpense = async (data) => {
        const expenseData = { ...data, project_id: project.id };
        if (editingExpense) {
            await Expense.update(editingExpense.id, expenseData);
        } else {
            await Expense.create(expenseData);
        }
        await loadData();
        setIsFormOpen(false);
        setEditingExpense(null);
    };

    const handleDeleteExpense = async (expenseId) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette dépense ?")) {
            await Expense.delete(expenseId);
            await loadData();
        }
    };

    const handleEditExpense = (expense) => {
        setEditingExpense(expense);
        setIsFormOpen(true);
    };

    const handleNewExpense = () => {
        setEditingExpense(null);
        setIsFormOpen(true);
    };

    const handleEditBudget = (categoryKey, currentAmount) => {
        setEditingBudgets(prev => ({ ...prev, [categoryKey]: true }));
        setTempBudgets(prev => ({ ...prev, [categoryKey]: currentAmount }));
    };

    const handleSaveBudget = async (categoryKey) => {
        const newAmount = Number(tempBudgets[categoryKey] || 0);
        const existingCategory = budgetCategories.find(bc => bc.category_key === categoryKey);
        const currentAmountOfEditedCategory = existingCategory?.allocated_budget || 0;

        const potentialTotalAllocated = totalAllocated - currentAmountOfEditedCategory + newAmount;

        if (potentialTotalAllocated > project.total_budget) {
            alert(`L'allocation totale (${formatCurrency(potentialTotalAllocated, projectCurrency)}) ne peut pas dépasser le budget total du projet (${formatCurrency(project.total_budget, projectCurrency)}).`);
            return;
        }

        try {
            if (existingCategory) {
                await BudgetCategory.update(existingCategory.id, { allocated_budget: newAmount });
            } else {
                // This case should only occur if a "default" category (from budgetStructure) had 0 allocated budget
                // and thus no BudgetCategory record, and is now being allocated a budget.
                // For manually added categories, a BudgetCategory record should always exist already.
                const categoryDisplayName = categoryData.find(c => c.key === categoryKey)?.name;
                const categoryName = categoryDisplayName || "Catégorie inconnue";

                await BudgetCategory.create({
                    project_id: project.id,
                    category_key: categoryKey,
                    category_name: categoryName,
                    allocated_budget: newAmount
                });
            }

            await loadData();
            setEditingBudgets(prev => ({ ...prev, [categoryKey]: false }));
            setTempBudgets(prev => ({ ...prev, [categoryKey]: undefined }));
        } catch (error) {
            console.error('Erreur lors de la sauvegarde du budget:', error);
        }
    };

    const handleCancelBudgetEdit = (categoryKey) => {
        setEditingBudgets(prev => ({ ...prev, [categoryKey]: false }));
        setTempBudgets(prev => ({ ...prev, [categoryKey]: undefined }));
    };

    const handleSaveNewCategory = async (data) => {
        try {
            await BudgetCategory.create(data);
            await loadData();
            setIsAddCategoryFormOpen(false);
        } catch (error) {
            console.error("Erreur lors de l'ajout de la catégorie", error);
        }
    };

    const handleViewExpenseDetail = (expense) => {
        setSelectedExpense(expense);
        setIsExpenseDetailOpen(true);
    };

    return (
        <>
            {isFormOpen && (
                <ExpenseForm
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    onSubmit={handleSaveExpense}
                    expense={editingExpense}
                    users={users}
                    projectCurrency={projectCurrency}
                />
            )}
            <AddBudgetCategoryForm
                isOpen={isAddCategoryFormOpen}
                onClose={() => setIsAddCategoryFormOpen(false)}
                onSubmit={handleSaveNewCategory}
                project={project}
                existingCategories={budgetCategories}
                totalAllocated={totalAllocated}
                projectCurrency={projectCurrency}
            />

            <ExpenseDetailModal
                isOpen={isExpenseDetailOpen}
                onClose={() => setIsExpenseDetailOpen(false)}
                expense={selectedExpense}
                users={users}
                projectCurrency={projectCurrency}
            />

            <div className="space-y-6">
                <Card className="shadow-lg border-0">
                    <CardHeader>
                        <CardTitle>Vue Synthétique du Budget</CardTitle>
                        <CardDescription>Analyse en cascade du budget global</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <BudgetWaterfallChart data={budgetMetrics} currencySymbol={currencySymbols[projectCurrency]} />
                    </CardContent>
                    <CardFooter className="grid grid-cols-4 gap-4 text-center">
                        <div><p className="font-bold text-lg">{formatCurrency(budgetMetrics.initial, projectCurrency)}</p><p className="text-sm text-gray-500">Budget Initial</p></div>
                        <div><p className="font-bold text-lg text-blue-600">{formatCurrency(budgetMetrics.engaged, projectCurrency)}</p><p className="text-sm text-gray-500">Engagé</p></div>
                        <div><p className="font-bold text-lg text-orange-600">{formatCurrency(budgetMetrics.spent, projectCurrency)}</p><p className="text-sm text-gray-500">Dépensé (Payé)</p></div>
                        <div><p className="font-bold text-lg text-green-600">{formatCurrency(budgetMetrics.remaining, projectCurrency)}</p><p className="text-sm text-gray-500">Restant</p></div>
                    </CardFooter>
                </Card>

                <Card className="shadow-lg border-0">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Allocation Budgétaire par Catégorie</CardTitle>
                            <CardDescription className="mt-1">
                                Budget alloué: {formatCurrency(totalAllocated, projectCurrency)} / {formatCurrency(project.total_budget, projectCurrency)}
                                {totalAllocated > project.total_budget && (
                                    <span className="text-red-600 font-medium flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-4 h-4" />
                      Dépassement de {formatCurrency(totalAllocated - project.total_budget, projectCurrency)}
                    </span>
                                )}
                            </CardDescription>
                        </div>
                        <Button variant="outline" className="gap-2" onClick={() => setIsAddCategoryFormOpen(true)}>
                            <Plus className="w-4 h-4"/> Ajouter une catégorie
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {categoryData.length > 0 ? categoryData.map(cat => (
                                <div key={cat.key} className="p-4 border rounded-lg">
                                    <div className="flex justify-between items-center mb-2">
                                        <p className="font-bold text-lg">{cat.name}</p>
                                        <div className="flex items-center gap-2">
                                            {editingBudgets[cat.key] ? (
                                                <>
                                                    <Input
                                                        type="number"
                                                        value={tempBudgets[cat.key] || ''}
                                                        onChange={(e) => setTempBudgets(prev => ({ ...prev, [cat.key]: e.target.value }))}
                                                        className="w-32"
                                                        placeholder="0"
                                                    />
                                                    <Button size="icon" variant="ghost" onClick={() => handleSaveBudget(cat.key)} className="text-green-600">
                                                        <Save className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" onClick={() => handleCancelBudgetEdit(cat.key)} className="text-gray-600">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-right">
                                                        <p className="font-semibold">{formatCurrency(cat.spent, projectCurrency)} / {formatCurrency(cat.allocated, projectCurrency)}</p>
                                                        <p className="text-sm text-gray-500">Dépensé / Alloué</p>
                                                    </div>
                                                    <Button size="icon" variant="ghost" onClick={() => handleEditBudget(cat.key, cat.allocated)} className="text-blue-600">
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <Progress value={cat.progress} indicatorClassName={cat.alertColor} />
                                    <p className="text-xs text-gray-500 mt-1">{cat.progress.toFixed(1)}% du budget alloué</p>
                                </div>
                            )) : (
                                <div className="text-center py-10 border-2 border-dashed rounded-lg">
                                    <p className="text-gray-500">Aucune catégorie budgétaire ajoutée.</p>
                                    <p className="text-sm text-gray-400 mt-1">Commencez par ajouter une catégorie pour allouer votre budget.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="shadow-lg border-0">
                    <CardHeader className="flex flex-row justify-between items-center">
                        <CardTitle>Détail des Dépenses</CardTitle>
                        <Button className="bg-teal-600 hover:bg-teal-700 gap-2" onClick={handleNewExpense}>
                            <Plus className="w-4 h-4" /> Nouvelle Dépense
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            {categoryData.map(cat => cat.expenses.length > 0 && (
                                <AccordionItem key={cat.key} value={cat.key}>
                                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                                        <div className="flex items-center gap-4">
                                            {cat.name}
                                            <Badge>{cat.expenses.length} dépense(s)</Badge>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Description</TableHead>
                                                    <TableHead>Fournisseur</TableHead>
                                                    <TableHead>Montant TTC</TableHead>
                                                    <TableHead>Statut</TableHead>
                                                    <TableHead>Documents</TableHead>
                                                    <TableHead>Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {cat.expenses.map(expense => (
                                                    <TableRow key={expense.id}>
                                                        <TableCell>
                                                            <p className="font-medium">{expense.description}</p>
                                                            <p className="text-sm text-gray-500">
                                                                {budgetStructure.flatMap(c => c.subCategories).find(s => s.key === expense.sub_category_key)?.name}
                                                            </p>
                                                        </TableCell>
                                                        <TableCell>{expense.supplier}</TableCell>
                                                        <TableCell>{formatCurrency(expense.amount_ttc, projectCurrency)}</TableCell>
                                                        <TableCell>
                                                            <Badge className={expenseStatusOptions[expense.status]?.color}>
                                                                {expenseStatusOptions[expense.status]?.label}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <FileText className="w-4 h-4 text-gray-500" />
                                                                <span className="text-sm">
                                  {expense.documents?.length || 0} doc(s)
                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="space-x-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleViewExpenseDetail(expense)}
                                                                title="Voir le détail"
                                                            >
                                                                <Eye className="w-4 h-4 text-blue-600" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleEditExpense(expense)}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDeleteExpense(expense.id)}
                                                                className="text-red-500"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
